import { ICourse2 } from "@et/types/Courses";
import { PAYMENT_TYPES_THEME } from "@et/types/Enums";
import { neonPink } from "@styles/themes/darkThemeBeautifulLettering";
import { graphql } from "gatsby";
const slug = 'fantasy-birds-blooms'
const title = 'Fantasy Birds and Blooms in Procreate'
const highlight1 = `#B77DC3`
const mainColor = `#4D286F`
const introOneTimePaymentUrl = 'https://courses.every-tuesday.com/purchase?product_id=5240405'
const oneTimePaymentUrl = 'https://courses.every-tuesday.com/purchase?product_id=5240402'
const course: ICourse2 = {
  theme: PAYMENT_TYPES_THEME.LIGHT,
  title,
  slug,
  videoId: `nfb07pgvxs`,
  keywords: [
    "Fantasy bird illustrations",
    "AI-generated floral designs",
    "Procreate fantasy art techniques",
    "Midjourney AI for artists",
    "Ethical AI art creation",
    "Digital painting of mythical birds",
    "Botanical illustrations in Procreate",
    "Fantasy blooms drawing",
    "Creative AI prompts for art",
    "DALL-E for fantasy art inspiration",
    "Sketching mythical creatures in Procreate",
    "Bird anatomy for artists",
    "Digital art workflows with AI",
    "Incorporating AI into digital painting",
    "Texturing techniques in Procreate",
    "Fantasy style bird painting",
    "Creating vibrant floral compositions",
    "Exploring fantasy themes in art",
    "Procreate tips for detailed bird art",
    "Combining nature and fantasy in art",
    "AI tools for creative expression",
    "Crafting unique bird characters",
    "Designing imaginary ecosystems",
    "Storytelling through digital art"
  ],
  price: '297',
  salePrice: '247',
  paymentPlan: {
    price: '80',
    perMonth: '4 Payments of $80/m',
    savings: '10%',
    salePrice: '85',
  },
  buyNowPayLater: {
    url: oneTimePaymentUrl,
    saleUrl: introOneTimePaymentUrl
  },
  motionMail: `https://s.mmgo.io/t/Csxm/scale_2x`, //use 2x version for PROD
  onSale: false,
  tagline: 'Ethically generate AI concepts and use them to inspire gorgeous, original paintings in Procreate!',
  classDescriptor: 'Ethically generate AI concepts and use them to inspire gorgeous, original paintings in Procreate!',
  intro: {
    imgName: 'procreate301/tablet-intro.png',
    imgAlt: 'Create custom modern lettering styles that are unique to you',
    bullets: [
      {
        title: 'Beginner Friendly',
        desc:
          'Step-by-step narration with closeups on all the details, so you feel confident in the process from start to finish.',
      },
      {
        title: 'A Versatile Process',
        desc:
          'Since every lettering style is different, this process has been fine-tuned to fit any situation. You’ll be able to achieve a realistic watercolor effect when you apply it to any style - hand lettered or typed.',
      },
      {
        title: 'Blend Options',
        desc:
          'You may want an ombré, one color, smooth transitions blend with one of your pieces and a multi-colored blend with water pockets on another. This course serves up 5 different blend options, so you’ll have plenty to choose from when you begin your next lettering piece.',
      },
      {
        title: 'Florals + Foliage Integration',
        desc:
          'Take your watercolor lettering even further by integrating florals and foliage! A bonus florals foundations module is included, so even if you’ve never painted florals before, you’ll be able to follow along and complete every project in the class.',
      },
    ],
  },
  projects: {
    beginner: [
      {
        title: 'Project 1',
        backgroundColor: `#c4d8da`,
        textColor: `#4a5457`,
        subheadColor: `#fff`,
        projectImg: `procreate301/project-1.jpg`,
        projectImgMobile: `procreate301/project-1.jpg`,
        type: '',
        desc:
          'We kick things off with a seashell symmetry full drop pattern. We\'ll set up our template together using the symmetry block brushes, paint in the elements, then make all edges seamless through pre-programmed selections. Once we have all of our gaps filled in, we\'ll drag it into the Pattern Playground for a final test.',
      },
      {
        title: 'Project 2',
        backgroundColor: `#fcbfb2`,
        textColor: `#4a5457`,
        subheadColor: `#fcb900`,
        projectImg: `procreate301/project-2.jpg`,
        projectImgMobile: `procreate301/project-2-mobile.jpg`,
        type: '',
        desc:
          'To reinforce what we covered in Project 1, we\'ll use another symmetry block brush - this time the flower shape, and create another full drop pattern. I share how I fix troublesome areas when elements moving in different directions collide and I explain my full thought process along with my reasons behind every decision I make. We reconfigure our pattern to make it seamless using our pre-programmed selections and do a final test using the Pattern Playground.',
      },
      {
        title: 'Project 3',
        backgroundColor: `#ffe3df`,
        textColor: `#4a5457`,
        subheadColor: `#f3ecdf`,
        projectImg: `procreate301/project-3.jpg`,
        projectImgMobile: `procreate301/project-3-mobile.jpg`,
        type: '',
        desc: 'In Project 3, we create a full drop repeat using an included composition template. Following the composition template, we\'re able to speed up our workflow and quickly adjust and adapt it to fit our needs. Once our elements are in place, we use our pre-programmed selections within the template to fill in the gaps, then test + adjust as needed using the Pattern Playground.',
      },
      {
        title: 'Project 4',
        projectImg: `procreate501/project-4.jpg`,
        projectImgMobile: `procreate301/project-4-mobile.jpg`,
        backgroundColor: `#dee5dd`,
        textColor: `#565857`,
        subheadColor: `#c56b00`,
        type: '',
        desc:
          'It\'s time to head into half drop patterns in Project 4! We\'ll work with a different composition template this time, pre-programmed with selections and set up specifically for half drops. After painting our elements in, we\'ll reorganize the template to fill in gaps using our selections, then test + adjust using the Pattern Playground.',
      },
      {
        title: 'Project 5',
        projectImg: `procreate301/project-5.jpg`,
        projectImgMobile: `procreate301/project-5-mobile.jpg`,
        backgroundColor: `#ffd5bc`,
        textColor: `#4a5457`,
        subheadColor: `#419187`,
        type: '',
        desc:
          'To round out our pattern types, Project 5 is all about the half brick. We\'ll pick a half brick specific composition template, lay in our artwork and go off script from the template as needed. We then reorganize our artwork using the pre-programmed selections to fill in gaps and test our completed half brick within the Pattern Playground.',
      },
    ],
    advanced: [],
  },
  curriculum: [
    {
      title: 'Welcome',
      videos: [
        {
          title: 'What You\'ll Learn/Course Overview',
          duration: '9:57',
        },
        {
          title: 'Bonuses: Location + Install Instructions',
          duration: '6:56',
        },
        {
          title: 'Acrylic Lovers Brush Set Overview',
          duration: '8:58',
        },
      ],
    },
    {
      title: 'Getting Started',
      videos: [
        {
          title: 'Canvas Size, Color Profile, Resolution',
          duration: '3:00',
        },
        {
          title: 'Gestures We\'ll Use',
          duration: '2:33',
        }
      ]
    },
    {
      title: 'Intro to AI: Midjourney',
      videos: [
        {
          title: 'Using AI Ethically/How AI for Image Generation Works',
          duration: '5:20',
        },
        {
          title: 'Getting Started with Midjourney',
          duration: '3:23',
        },
      ],
    },
    {
      title: 'Generating Concepts with AI',
      videos: [
        {
          title: 'Intro to Using Prompts',
          duration: '7:34',
        },
        {
          title: 'Fantasy Floral Prompts',
          duration: '10:20',
        },
        {
          title: 'Image Prompts: Creating Fantasy Birds & Blooms Concepts',
          duration: '7:12',
        },
        {
          title: 'Permutation Prompts + Exporting Your Renders',
          duration: '14:16',
        }
      ],
    },
    {
      title: 'Intro to DALL-E',
      videos: [
        {
          title: 'How to use DALL-E (Free and Paid)',
          duration: '5:58',
        }
      ]
    },
    {
      title: 'Project 1: Fantasy Floral Blooms',
      videos: [
        {
          title: 'Create the Composition Sketch: Part 1',
          duration: '9:01',
        },
        {
          title: 'Create the Composition Sketch: Part 2',
          duration: '5:07',
        },
        {
          title: 'Hero Bloom: Part 1',
          duration: '8:18',
        },
        {
          title: 'Hero Bloom: Part 2',
          duration: '7:03',
        },
        {
          title: 'Hero Bloom: Part 3',
          duration: '9:42',
        },
        {
          title: 'Hero Side Bloom: Part 1',
          duration: '18:43',
        },
        {
          title: 'Hero Side Bloom: Part 2',
          duration: '14:01',
        },
        {
          title: 'Hero Top Bloom: Part 1 (with petal fold demo)',
          duration: '11:16',
        },
        {
          title: 'Hero Top Bloom: Part 2',
          duration: '13:03',
        },
        {
          title: 'Hero Top Bloom: Part 3',
          duration: '5:59',
        },
        {
          title: 'Paint Supporting Floral: Rose',
          duration: '13:08',
        },
        {
          title: 'Paint Supporting Floral: Thistle',
          duration: '7:29',
        },
        {
          title: 'Painting Foliage: Part 1',
          duration: '16:32',
        },
        {
          title: 'Painting Foliage: Part 2',
          duration: '14:33',
        },
        {
          title: 'Painting Foliage: Part 3',
          duration: '10:34',
        },
        {
          title: 'Painting Foliage: Part 4',
          duration: '8:39',
        },
        {
          title: 'Finishing Touches',
          duration: '6:52',
        },
      ],
    },
    {
      title: 'Bonus: Bird Drawing Basics',
      videos: [
        {
          title: 'Bird Anatomy/Drawing from a Reference Photo',
          duration: '8:59',
        },
        {
          title: 'Finalizing your Bird Drawing',
          duration: '10:25',
        },
      ],
    },
    {
      title: 'Project 2: Fantasy Style Chickadee',
      videos: [
        {
          title: 'Gathering References',
          duration: '2:17',
        },
        {
          title: 'Establish a Painting Plan + Create the Color Block',
          duration: '9:22',
        },
        {
          title: 'Blend the Color Block',
          duration: '6:38',
        },
        {
          title: 'Adding Detail + Texture to the Belly',
          duration: '9:28',
        },
        {
          title: 'Adding Detail + Texture to the Head',
          duration: '10:28',
        },
        {
          title: 'Wing Details',
          duration: '8:43',
        },
        {
          title: 'Finishing Touches',
          duration: '14:43',
        },
      ]
    },
    {
      title: 'Project 3: Fantasy Bird + Blooms',
      videos: [
        {
          title: 'Create the Composition',
          duration: '19:07',
        },
        {
          title: 'Add the References',
          duration: '2:32',
        },
        {
          title: 'Color Blocking the Belly',
          duration: '6:44',
        },
        {
          title: 'Color Blocking the Wing + Claw',
          duration: '4:50',
        },
        {
          title: 'Bluebird Details: Part 1',
          duration: '11:53',
        },
        {
          title: 'Bluebird Details: Part 2',
          duration: '10:34',
        },
        {
          title: 'Right Side Florals: Part 1',
          duration: '4:48',
        },
        {
          title: 'Right Side Florals: Part 2',
          duration: '14:32',
        },
        {
          title: 'Right Side Florals: Part 3',
          duration: '4:04',
        },
        {
          title: 'Right Side Florals: Part 4',
          duration: '4:48',
        },
        {
          title: 'Right Side Foliage: Leafy Branches',
          duration: '7:58',
        },
        {
          title: 'Right Side Leaves: Part 1',
          duration: '8:56',
        },
        {
          title: 'Right Side Leaves: Part 2',
          duration: '9:49',
        },
        {
          title: 'Left Side Orange Flower: Part 1',
          duration: '7:38',
        },
        {
          title: 'Left Side Orange Flower: Part 2',
          duration: '8:14',
        },
        {
          title: 'Left Side Pink Flower',
          duration: '14:00',
        },
        {
          title: 'Left Side Foliage: Flower Branch',
          duration: '9:45',
        },
        {
          title: 'Left Side Foliage: Leafy Branch',
          duration: '6:46',
        },
        {
          title: 'Left Side Foliage: Leaves',
          duration: '7:29',
        },
        {
          title: 'Adding the Perch Branch',
          duration: '3:46',
        },
        {
          title: 'Adding Final Details + Finishing Up',
          duration: '12:16',
        },
      ]
    },
    {
      title: 'Bonus: Project Walkthrough #1',
      videos: [
        {
          title: 'Goldfinch Compositions',
          duration: '5:08',
        },
      ]
    },
    {
      title: 'Bonus: Project Walkthrough #2',
      videos: [
        {
          title: 'European Robin Composition',
          duration: '5:55',
        },
      ]
    },
    {
      title: 'Thank You',
      videos: [
        {
          title: 'Thank You + Next Steps',
          duration: '2:11',
        },
      ]
    },
  ],
  bonuses: {
    headerImgName: `procreate301/bonus-title.png`,
    imgAlt: 'Messy Watercolors in Procreate Bonuses',
    gridType: 1,
    items: [
      {
        title: 'Acrylic Lovers Brush Set + Extended License',
        desc: '<p>The full, 80 piece brush set, filled with sketching, painting, smudging and stamp brushes are included with your enrollment so you can follow along exactly. An extended license is included, so you can use the brushes to create artwork you plan to sell without limit.</p>',
        imgName: 'birds-blooms/bonuses/bonus-1.jpg',
        bgColor: '#FFF6EE'
      },
      {
        title: 'Bird Poses Brush Set',
        desc: '<p>30 bird pose stamps in 9 bird varieties is provided to make composition creation immensely easier! Simply stamp in the right pose based on your render, then build up your florals and foliage around it.</p>',
        imgName: 'birds-blooms/bonuses/bonus-2.jpg',
        bgColor: '#FFF6EE'
      },
      {
        title: '50+ AI Renders',
        desc: '<p>All project AI renders are provided, in addition to over 50 more, so you can use the same skills from the course in your future pieces, whether you plan to generate your own AI reference images or not.</p>',
        imgName: 'birds-blooms/bonuses/bonus-3.jpg',
        bgColor: '#FFF6EE'
      },
      {
        title: "Midjourney Cheat Sheets",
        desc: '<p>A multi-page PDF cheat sheet covering Midjourney settings, text prompts, parameters, image prompts and permutation prompts.</p>',
        imgName: 'birds-blooms/bonuses/bonus-4.jpg',
        bgColor: '#FFF6EE'
      },
      {
        title: 'Bird Anatomy Cheat Sheets',
        desc: '<p>A multi-page pdf with detailed drawings to aid in sketching your own birds from scratch!</p>',
        imgName: 'birds-blooms/bonuses/bonus-5.jpg',
        bgColor: '#FFF6EE'
      },
      {
        title: 'Copy/Paste Sample Prompts',
        desc: '<p>All of my favorite fantasy style Midjourney prompts in an easy-to-pull-from place.</p>',
        imgName: 'birds-blooms/bonuses/bonus-6.jpg',
        bgColor: '#FFF6EE'
      },
      {
        title: 'Bird List',
        desc: '<p>Need some extra bird types you can render images with? Take a look at this list and enjoy seeing those birds come to life!</p>',
        imgName: 'birds-blooms/bonuses/bonus-7.jpg',
        bgColor: '#FFF6EE'
      },
      {
        title: 'Bonus Project Walk-throughs',
        desc: '<p>In addition to the course projects, you’ll see how 2 additional birds + blooms projects are built with full narration.</p>',
        imgName: 'birds-blooms/bonuses/bonus-8.jpg',
        bgColor: '#FFF6EE'
      },
      {
        title: 'Private Facebook Community',
        desc: '<p>A group of Procreate creators to paint beautiful fantasy birds + blooms alongside. The perfect place to find answers to questions, get feedback on your work and a non stop source of support from the kindest Procreate makers. I’m always in there, too!</p>',
        imgName: 'birds-blooms/bonuses/bonus-9.jpg',
        bgColor: '#FFF6EE'
      },

    ],
  },
  courseCard: {
    title: 'Messy Watercolors in Procreate',
    backgroundImgName: ``,
    backgroundColor: '#65807c',
    cardImgName: `procreate301/watercolor-class-card-4.jpg`,
    desc: 'Paint vibrant and messy eye candy watercolor artwork in Procreate! Reference traditional methods while utilizing digital advantages like blend modes, selective color adjustments and layers. Follow along as each project builds in complexity with technique, process and levels of messiness. In addition to painting, you’ll learn and use multiple layers, blend modes, selections, recoloring, saturation adjustments and warping to create stunning artwork using an efficient process. Finish the course by learning how to prepare your artwork for print using a home printer or online print services to enjoy your art beyond the screen.',
    bullets: [
      'Acrylic Lovers Brush Set + Extended License',
      '30 Bird Poses Stamp Set',
      '2 Bonus Project Walk-throughs',
      'Bird Anatomy Cheat Sheets',
      'Bird Drawing Exercises',
      'Midjourney Cheat Sheets',
      'Full Ethical Use Midjourney Tutorial Module',
      'Intro to AI Full Walkthrough for Beginners',
      'Copy/Paste Fantasy Style Text Prompts',
      'DALL-E Free + Paid Version Overview',
      'Text, Image + Permutation Prompting',
      'Composition Sketches using Multiple Renders',
      'Canvas Setup + Common Gestures',
      '3 In-Depth Comprehensive Projects',
      'Petals vs. Leaves Blending Demos',
      'Fantasy Style Feather Painting',
      '20+ Birds List',
      '50+ AI Renders with Permission to Use',
      'Membership in Our Private Facebook Community',
      'Lifetime Access',
    ],
    brush: {
      enabled: true,
      image: {
        pencil: `apple2-pencil.png`,
        shadow: `sell-card-pencil-reflection-light.png`,
      },
    },
    priceColor: mainColor,
    saleColor: neonPink,
    stats: {
      modules: `#cc836f`,
      videos: `#758384`,
      hours: `#a79e8d`,
    },
  },
  faqs: [
    {
      question: 'I know nothing about AI. Can I still take this course?',
      answer: '<p>Yes! All AI walkthroughs were recorded with the absolute beginner in mind. Whether you’ve used Midjourney before or not, you’ll be able to follow along every step of the way. I’ve provided all the renders you’ll need to take the course (along with over 50 more!), so there’s no need to use an AI service, but you’ll know how if you want to!</p>',
    },
    {
      question: 'I’m brand new to Procreate, will I be able to follow along?',
      answer: '<p>The course was created with beginners in mind, though if you\'re brand new to Procreate, check out my <a target="_blank" href="https://learn.every-tuesday.com/procreate-for-beginners">free Procreate for Beginners course</a> first and you\'ll cruise through more comfortably.</p>',
    },
    {
      question: 'What do I need to take this course?',
      answer: '<p>You\'ll need an iPad that supports pressure sensitivity along with a compatible stylus (<a target="_blank" href="https://support.apple.com/en-us/HT211029">check out this list</a>) and the Procreate app (v.5.3.5 or newer) installed. That\'s it!</p>',
    },
    {
      question: 'I don\'t think I have time for this?',
      answer: '<p>All Every Tuesday courses come with lifetime access, so you can take it at the time and pace that works best for you. It\'s yours for the long haul.</p>',
    },
    {
      question: 'Do you offer any refunds?',
      answer: '<p>This course is backed by our 14 day guarantee. If you take the course and feel you haven’t learned anything new, send us an email before the 14 days is up for a full refund.</p>',
    },
    {
      question: 'Does this class come with subtitles/closed captions?',
      answer: '<p>Yep! English subtitles are included for every video</p>'
    },
    {
      question: 'How long do I get access?',
      answer: '<p>You buy something, you get to keep it around here.</p>',
    },
    {
      question: 'Is this a live or pre-recorded class?',
      answer: '<p>All videos are pre-recorded, so you can watch it whenever it works best for you.</p>',
    },
    {
      question: 'Can I sell the class projects I recreate on my own?',
      answer: '<p>Since these projects are inspired by AI renders, you may recreate and sell any artwork you make from this course’s projects.</p>',
    },
    {
      question: 'I have more questions, how can I get in touch?',
      answer: '<p>Head to <a target="_blank" href="https://every-tuesday.com/contact">my contact page</a> and drop me your Q\'s - I\'m just an email away.</p>',
    },
  ],
  testimonials: [],
  letsDoThis: {
    imgName: 'procreate301/lets-do-this-2.jpg',
    altTag: 'Enroll in Messy Watercolors for Procreate today!',
    text: 'Welcome to Flat Florals in Procreate, your complete guide to painting simplified, bold and beautiful florals.',
  },
  sellCard: {
    priceColor: '#5f1212',
    saleColor: neonPink,
    cardImg: '',
    saleLink: introOneTimePaymentUrl,
    link: oneTimePaymentUrl,
    paymentPlanLink: 'https://courses.every-tuesday.com/purchase?product_id=5240403',
    paymentPlanSaleLink: 'https://courses.every-tuesday.com/purchase?product_id=5240411',
    customBtnBackgroundColor: 'var(--ff-teal-600)',
    h2Color: `#5f1212`,
    hoverColor: {
      h2: `#fff`,
      text: 'var(--ff-teal-600)',
      overlay: `var(--ff-teal-400)`,
      cardDescription: '#fff'
    },
    bullets: [
      'Instant access to all videos and bonuses',
      'Full Bouquet Maker brush set (150+ brushes)',
      'Bonus applications module (3 projects)',
      'Private Facebook community',
      'Extended License of the brush set ($40 value)',
      'Seamless patterns with transparent backgrounds process',
      'Rose simplification tutorial',
      'Composition design tutorials',
      '14 day money back guarantee',
      'Flat style cheat sheets (pdf)',
      'All project + demo color palettes',
      'English subtitles on all videos',
      'Basic + complex style practice exercises (12 total)',
    ],
  },
}
export const gqlFragmentBirdsBloomsWMCD_Images = graphql`
    fragment FragmentBirdsBloomsWMCD_Images on Query {
        WMCD1:imgixImage(url: "birds-blooms/different/diff-1.jpg") {
             gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {}, srcSetMaxWidth: 400)
        },
        WMCD2:imgixImage(url: "birds-blooms/different/diff-2.jpg") {
             gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
              fit: "crop",
              w:400,
              auto: "compress"
          }, srcSetMaxWidth: 400)
        },
        WMCD3:imgixImage(url: "birds-blooms/different/diff-3.jpg") {
             gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
              fit: "crop",
              w:400,
              auto: "compress"
          }, srcSetMaxWidth: 400)
        },
        WMCD4:imgixImage(url: "birds-blooms/different/diff-4.jpg") {
             gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
              fit: "crop",
              w:400,
              auto: "compress"
          }, srcSetMaxWidth: 400)
        },
        WMCD5:imgixImage(url: "birds-blooms/different/diff-5.jpg") {
             gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
              fit: "crop",
              w:400,
              auto: "compress"
          }, srcSetMaxWidth: 400)
        },
        WMCD6:imgixImage(url: "birds-blooms/different/diff-6.jpg") {
             gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
              fit: "crop",
              w:400,
              auto: "compress"
          }, srcSetMaxWidth: 400)
        },
        WMCD7:imgixImage(url: "birds-blooms/different/diff-7.jpg") {
             gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
              fit: "crop",
              w:400,
              auto: "compress"
          }, srcSetMaxWidth: 400)
        },
        WMCD8:imgixImage(url: "birds-blooms/different/diff-8.jpg") {
             gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
              fit: "crop",
              w:400,
              auto: "compress"
          }, srcSetMaxWidth: 400)
        },
        WMCD9:imgixImage(url: "birds-blooms/different/diff-9.jpg") {
             gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
              fit: "crop",
              w:400,
              auto: "compress"
          }, srcSetMaxWidth: 400)
        },
        WMCD10:imgixImage(url: "birds-blooms/different/diff-10.jpg") {
             gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
              fit: "crop",
              w:400,
              auto: "compress"
          }, srcSetMaxWidth: 400)
        },
        WMCD11:imgixImage(url: "birds-blooms/different/diff-11.jpg") {
             gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
              fit: "crop",
              w:400,
              auto: "compress"
          }, srcSetMaxWidth: 400)
        },
        WMCD12:imgixImage(url: "birds-blooms/different/diff-12.jpg") {
             gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
              fit: "crop",
              w:400,
              auto: "compress"
          }, srcSetMaxWidth: 400)
        }

    }
`
export const gqlFragmentBirdsBloomsWYL = graphql`
    fragment FragmentBirdsBloomsWYL on Query {
        wylGridImage1:imgixImage(url: "birds-blooms/wyl/wyl-1.jpg") {
             gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
              fit: "crop",
              w:400,
              auto: "compress"
          }, srcSetMaxWidth: 400)
        }
        wylGridImage2:imgixImage(url: "birds-blooms/wyl/wyl-2.jpg") {
             gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
              fit: "crop",
              w:400,
              auto: "compress"
          }, srcSetMaxWidth: 400)
        }
        wylGridImage3:imgixImage(url: "birds-blooms/wyl/wyl-3.jpg") {
             gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
              fit: "crop",
              w:400,
              auto: "compress"
          }, srcSetMaxWidth: 400)
        }
        wylGridImage4:imgixImage(url: "birds-blooms/wyl/wyl-4.jpg") {
             gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
              fit: "crop",
              w:400,
              auto: "compress"
          }, srcSetMaxWidth: 400)
        }
        wylGridImage5:imgixImage(url: "birds-blooms/wyl/wyl-6.jpg") {
             gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
              fit: "crop",
              w:400,
              auto: "compress"
          }, srcSetMaxWidth: 400)
        }
        wylGridImage6:imgixImage(url: "birds-blooms/wyl/wyl-5.jpg") {
             gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
              fit: "crop",
              w:400,
              auto: "compress"
          }, srcSetMaxWidth: 400)
        }
        wylGridImage7:imgixImage(url: "birds-blooms/wyl/wyl-7.jpg") {
             gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
              fit: "crop",
              w:400,
              auto: "compress"
          }, srcSetMaxWidth: 400)
        }
        wylGridImage8:imgixImage(url: "birds-blooms/wyl/wyl-8.jpg") {
             gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
              fit: "crop",
              w:400,
              auto: "compress"
          }, srcSetMaxWidth: 400)
        }
        wylGridImage9:imgixImage(url: "birds-blooms/wyl/wyl-9.jpg") {
          gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
              fit: "crop",
              w:400,
              auto: "compress"
          }, srcSetMaxWidth: 400)
        }
        wylGridImage10:imgixImage(url: "birds-blooms/wyl/wyl-10.jpg") {
          gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
              fit: "crop",
              w:400,
              auto: "compress"
          }, srcSetMaxWidth: 400)
        }
        wylGridImage11:imgixImage(url: "birds-blooms/wyl/wyl-11.jpg") {
          gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
              fit: "crop",
              w:400,
              auto: "compress"
          }, srcSetMaxWidth: 400)
        }
        wylGridImage12:imgixImage(url: "birds-blooms/wyl/wyl-12.jpg") {
          gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
              fit: "crop",
              w:400,
              auto: "compress"
          }, srcSetMaxWidth: 400)
        }
        wylSquareImage1:imgixImage(url: "birds-blooms/wyl/wyl-sq-2.jpg") {
            gatsbyImageData(width:600, layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
              fit: "crop",
              w:600,
              auto: "compress"
          }, srcSetMaxWidth: 600)
        }
        wylSquareImage2:imgixImage(url: "birds-blooms/wyl/wyl-sq-3.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 800
            })
        }
    }
`
export const gqlFragmentBirdsBloomsBrushTips = graphql`
    fragment FragmentBirdsBloomsBrushTips on Query {
        brushTip1:imgixImage(url: "birds-blooms/brushes/brush-1-1.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
            },srcSetMaxWidth: 200)
        }
        brushTip2:imgixImage(url: "birds-blooms/brushes/brush-1-2.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
            },srcSetMaxWidth: 200)
        }
        brushTip3:imgixImage(url: "birds-blooms/brushes/brush-1-3.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
            },srcSetMaxWidth: 200)
        }
        brushTip4:imgixImage(url: "birds-blooms/brushes/brush-1-4.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
            },srcSetMaxWidth: 200)
        }
        brushTip5:imgixImage(url: "birds-blooms/brushes/brush-1-5.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
            },srcSetMaxWidth: 200)
        }
        brushTip6:imgixImage(url: "birds-blooms/brushes/brush-1-6.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
            },srcSetMaxWidth: 200)
        }
        brushTip7:imgixImage(url: "birds-blooms/brushes/brush-1-7.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
            },srcSetMaxWidth: 200)
        }
        brushTip8:imgixImage(url: "birds-blooms/brushes/brush-1-8.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
            },srcSetMaxWidth: 200)
        }
        brushTip9:imgixImage(url: "birds-blooms/brushes/brush-1-9.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
            },srcSetMaxWidth: 200)
        }
        brushTip10:imgixImage(url: "birds-blooms/brushes/brush-1-10.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
            },srcSetMaxWidth: 200)
        }
        brushTip11:imgixImage(url: "birds-blooms/brushes/brush-1-11.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
            },srcSetMaxWidth: 200)
        }
        brushTip12:imgixImage(url: "birds-blooms/brushes/brush-1-12.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
            },srcSetMaxWidth: 200)
        }
        brushTip13:imgixImage(url: "birds-blooms/brushes/brush-1-13.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
            },srcSetMaxWidth: 200)
        }
        brushTip14:imgixImage(url: "birds-blooms/brushes/brush-1-14.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
            },srcSetMaxWidth: 200)
        }
        brushTip15:imgixImage(url: "birds-blooms/brushes/brush-1-15.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
            },srcSetMaxWidth: 200)
        }
        brushTip2_1:imgixImage(url: "birds-blooms/brushes/brush-2-1.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
            },srcSetMaxWidth: 200)
        }
        brushTip2_2:imgixImage(url: "birds-blooms/brushes/brush-2-2.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
            },srcSetMaxWidth: 200)
        }
        brushTip2_3:imgixImage(url: "birds-blooms/brushes/brush-2-3.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
            },srcSetMaxWidth: 200)
        }
        brushTip2_4:imgixImage(url: "birds-blooms/brushes/brush-2-4.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
            },srcSetMaxWidth: 200)
        }
        brushTip2_5:imgixImage(url: "birds-blooms/brushes/brush-2-5.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
            },srcSetMaxWidth: 200)
        }
        brushTip2_6:imgixImage(url: "birds-blooms/brushes/brush-2-6.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
            },srcSetMaxWidth: 200)
        }
        brushTip2_7:imgixImage(url: "birds-blooms/brushes/brush-2-7.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
            },srcSetMaxWidth: 200)
        }
        brushTip2_8:imgixImage(url: "birds-blooms/brushes/brush-2-8.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
            },srcSetMaxWidth: 200)
        }
        brushTip2_9:imgixImage(url: "birds-blooms/brushes/brush-2-9.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
            },srcSetMaxWidth: 200)
        }
        brushTip2_10:imgixImage(url: "birds-blooms/brushes/brush-2-10.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
            },srcSetMaxWidth: 200)
        }
        brushTip2_11:imgixImage(url: "birds-blooms/brushes/brush-2-11.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
            },srcSetMaxWidth: 200)
        }
        brushTip2_12:imgixImage(url: "birds-blooms/brushes/brush-2-12.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
            },srcSetMaxWidth: 200)
        }
        brushTip2_13:imgixImage(url: "birds-blooms/brushes/brush-2-13.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
            },srcSetMaxWidth: 200)
        }
        brushTip2_14:imgixImage(url: "birds-blooms/brushes/brush-2-14.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
            },srcSetMaxWidth: 200)
        }
        brushTip2_15:imgixImage(url: "birds-blooms/brushes/brush-2-15.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
            },srcSetMaxWidth: 200)
        }
        brushTip2_16:imgixImage(url: "birds-blooms/brushes/brush-2-16.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
            },srcSetMaxWidth: 200)
        }
        brushTip2_17:imgixImage(url: "birds-blooms/brushes/brush-2-17.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
            },srcSetMaxWidth: 200)
        }
        brushTip2_18:imgixImage(url: "birds-blooms/brushes/brush-2-18.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
            },srcSetMaxWidth: 200)
        }
        brushTip2_19:imgixImage(url: "birds-blooms/brushes/brush-2-19.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
            },srcSetMaxWidth: 200)
        }
        brushTip2_20:imgixImage(url: "birds-blooms/brushes/brush-2-20.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
            },srcSetMaxWidth: 200)
        }
        brushTip2_21:imgixImage(url: "birds-blooms/brushes/brush-2-21.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
            },srcSetMaxWidth: 200)
        }
        brushTip2_22:imgixImage(url: "birds-blooms/brushes/brush-2-22.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
            },srcSetMaxWidth: 200)
        }
        brushTip2_23:imgixImage(url: "birds-blooms/brushes/brush-2-23.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
            },srcSetMaxWidth: 200)
        }
        brushTip2_24:imgixImage(url: "birds-blooms/brushes/brush-2-24.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
            },srcSetMaxWidth: 200)
        }
        brushTip2_25:imgixImage(url: "birds-blooms/brushes/brush-2-25.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
            },srcSetMaxWidth: 200)
        }
        brushTip2_26:imgixImage(url: "birds-blooms/brushes/brush-2-26.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
            },srcSetMaxWidth: 200)
        }
        brushTip2_27:imgixImage(url: "birds-blooms/brushes/brush-2-27.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
            },srcSetMaxWidth: 200)
        }
        brushTip2_28:imgixImage(url: "birds-blooms/brushes/brush-2-28.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
            },srcSetMaxWidth: 200)
        }
        brushTip2_29:imgixImage(url: "birds-blooms/brushes/brush-2-29.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
            },srcSetMaxWidth: 200)
        }
        brushTip2_30:imgixImage(url: "birds-blooms/brushes/brush-2-30.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
            },srcSetMaxWidth: 200)
        }
        brushTip2_31:imgixImage(url: "birds-blooms/brushes/brush-2-31.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
            },srcSetMaxWidth: 200)
        }
        brushTip2_32:imgixImage(url: "birds-blooms/brushes/brush-2-32.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
            },srcSetMaxWidth: 200)
        }
        messy_stroke_1:imgixImage(url: "birds-blooms/brushes/brush-3-1.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
            },srcSetMaxWidth: 200)
        }
        messy_stroke_2:imgixImage(url: "birds-blooms/brushes/brush-3-2.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
            },srcSetMaxWidth: 200)
        }
        messy_stroke_3:imgixImage(url: "birds-blooms/brushes/brush-3-3.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
            },srcSetMaxWidth: 200)
        }
        messy_stroke_4:imgixImage(url: "birds-blooms/brushes/brush-3-4.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
            },srcSetMaxWidth: 200)
        }
        messy_stroke_5:imgixImage(url: "birds-blooms/brushes/brush-3-5.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
            },srcSetMaxWidth: 200)
        }
        messy_stroke_6:imgixImage(url: "birds-blooms/brushes/brush-3-6.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
            },srcSetMaxWidth: 200)
        }
        messy_stroke_7:imgixImage(url: "birds-blooms/brushes/brush-3-7.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
            },srcSetMaxWidth: 200)
        }
        messy_stroke_8:imgixImage(url: "birds-blooms/brushes/brush-3-8.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
            },srcSetMaxWidth: 200)
        }
        messy_stroke_9:imgixImage(url: "birds-blooms/brushes/brush-3-9.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
            },srcSetMaxWidth: 200)
        }
        messy_stroke_10:imgixImage(url: "birds-blooms/brushes/brush-3-10.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
            },srcSetMaxWidth: 200)
        }
        messy_stroke_11:imgixImage(url: "birds-blooms/brushes/brush-3-11.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
            },srcSetMaxWidth: 200)
        }
        messy_stroke_12:imgixImage(url: "birds-blooms/brushes/brush-3-12.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
            },srcSetMaxWidth: 200)
        }
        messy_stroke_13:imgixImage(url: "birds-blooms/brushes/brush-3-13.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
            },srcSetMaxWidth: 200)
        }
        messy_stroke_14:imgixImage(url: "birds-blooms/brushes/brush-3-14.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
            },srcSetMaxWidth: 200)
        }
        messy_stroke_15:imgixImage(url: "birds-blooms/brushes/brush-3-15.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
            },srcSetMaxWidth: 200)
        }
        messy_stroke_16:imgixImage(url: "birds-blooms/brushes/brush-3-16.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
            },srcSetMaxWidth: 200)
        }
        overlay_1:imgixImage(url: "birds-blooms/brushes/brush-4-1.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
            },srcSetMaxWidth: 200)
        }
        overlay_2:imgixImage(url: "birds-blooms/brushes/brush-4-2.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
            },srcSetMaxWidth: 200)
        }
        overlay_3:imgixImage(url: "birds-blooms/brushes/brush-4-3.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
            },srcSetMaxWidth: 200)
        }
        overlay_4:imgixImage(url: "birds-blooms/brushes/brush-4-4.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
            },srcSetMaxWidth: 200)
        }
        overlay_5:imgixImage(url: "birds-blooms/brushes/brush-4-5.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
            },srcSetMaxWidth: 200)
        }
        overlay_6:imgixImage(url: "birds-blooms/brushes/brush-4-6.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
            },srcSetMaxWidth: 200)
        }
        overlay_7:imgixImage(url: "birds-blooms/brushes/brush-4-7.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
            },srcSetMaxWidth: 200)
        }
        overlay_8:imgixImage(url: "birds-blooms/brushes/brush-4-8.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
            },srcSetMaxWidth: 200)
        }
        overlay_9:imgixImage(url: "birds-blooms/brushes/brush-4-9.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
            },srcSetMaxWidth: 200)
        }
        wash_1:imgixImage(url: "birds-blooms/brushes/brush-5-1.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
            },srcSetMaxWidth: 200)
        }
        wash_2:imgixImage(url: "birds-blooms/brushes/brush-5-2.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
            },srcSetMaxWidth: 200)
        }
        wash_3:imgixImage(url: "birds-blooms/brushes/brush-5-3.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
            },srcSetMaxWidth: 200)
        }
        wash_4:imgixImage(url: "birds-blooms/brushes/brush-5-4.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
            },srcSetMaxWidth: 200)
        }
        wash_5:imgixImage(url: "birds-blooms/brushes/brush-5-5.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
            },srcSetMaxWidth: 200)
        }
        wash_6:imgixImage(url: "birds-blooms/brushes/brush-5-6.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
            },srcSetMaxWidth: 200)
        }
        wash_7:imgixImage(url: "birds-blooms/brushes/brush-5-7.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
            },srcSetMaxWidth: 200)
        }
        wash_8:imgixImage(url: "birds-blooms/brushes/brush-5-8.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
            },srcSetMaxWidth: 200)
        }
        wash_9:imgixImage(url: "birds-blooms/brushes/brush-5-9.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
            },srcSetMaxWidth: 200)
        }
    }
`

export const gqlFragmentBirdsPoses = graphql`
    fragment FragmentBirdsPoses on Query {
        northern_cardinal_1:imgixImage(url: "birds-blooms/bird-poses/bird-pose-1-1.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
            },srcSetMaxWidth: 200)
        }
        northern_cardinal_2:imgixImage(url: "birds-blooms/bird-poses/bird-pose-1-2.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
            },srcSetMaxWidth: 200)
        }
        northern_cardinal_3:imgixImage(url: "birds-blooms/bird-poses/bird-pose-1-3.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
            },srcSetMaxWidth: 200)
        }
        northern_cardinal_4:imgixImage(url: "birds-blooms/bird-poses/bird-pose-1-4.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
            },srcSetMaxWidth: 200)
        }
        northern_cardinal_5:imgixImage(url: "birds-blooms/bird-poses/bird-pose-1-5.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
            },srcSetMaxWidth: 200)
        }
        american_bluebird_1:imgixImage(url: "birds-blooms/bird-poses/bird-pose-2-1.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
            },srcSetMaxWidth: 200)
        }
        american_bluebird_2:imgixImage(url: "birds-blooms/bird-poses/bird-pose-2-2.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
            },srcSetMaxWidth: 200)
        }
        american_bluebird_3:imgixImage(url: "birds-blooms/bird-poses/bird-pose-2-3.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
            },srcSetMaxWidth: 200)
        }
        american_bluebird_4:imgixImage(url: "birds-blooms/bird-poses/bird-pose-2-4.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
            },srcSetMaxWidth: 200)
        }
        american_robin_1:imgixImage(url: "birds-blooms/bird-poses/bird-pose-3-1.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
            },srcSetMaxWidth: 200)
        }
        american_robin_2:imgixImage(url: "birds-blooms/bird-poses/bird-pose-3-2.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
            },srcSetMaxWidth: 200)
        }
        euro_robin_1:imgixImage(url: "birds-blooms/bird-poses/bird-pose-4-1.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
            },srcSetMaxWidth: 200)
        }
        euro_robin_2:imgixImage(url: "birds-blooms/bird-poses/bird-pose-4-2.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
            },srcSetMaxWidth: 200)
        }
        euro_robin_3:imgixImage(url: "birds-blooms/bird-poses/bird-pose-4-3.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
            },srcSetMaxWidth: 200)
        }
        euro_robin_4:imgixImage(url: "birds-blooms/bird-poses/bird-pose-4-4.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
            },srcSetMaxWidth: 200)
        }
        tufted_1:imgixImage(url: "birds-blooms/bird-poses/bird-pose-5-1.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
            },srcSetMaxWidth: 200)
        }
        tufted_2:imgixImage(url: "birds-blooms/bird-poses/bird-pose-5-2.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
            },srcSetMaxWidth: 200)
        }
        tufted_3:imgixImage(url: "birds-blooms/bird-poses/bird-pose-5-3.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
            },srcSetMaxWidth: 200)
        }
        tufted_4:imgixImage(url: "birds-blooms/bird-poses/bird-pose-5-4.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
            },srcSetMaxWidth: 200)
        }
        american_goldfinch_1:imgixImage(url: "birds-blooms/bird-poses/bird-pose-6-1.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
            },srcSetMaxWidth: 200)
        }
        american_goldfinch_2:imgixImage(url: "birds-blooms/bird-poses/bird-pose-6-2.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
            },srcSetMaxWidth: 200)
        }
        american_goldfinch_3:imgixImage(url: "birds-blooms/bird-poses/bird-pose-6-3.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
            },srcSetMaxWidth: 200)
        }
        american_goldfinch_4:imgixImage(url: "birds-blooms/bird-poses/bird-pose-6-4.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
            },srcSetMaxWidth: 200)
        }
        purple_finch_1:imgixImage(url: "birds-blooms/bird-poses/bird-pose-7-1.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
            },srcSetMaxWidth: 200)
        }
        purple_finch_2:imgixImage(url: "birds-blooms/bird-poses/bird-pose-7-2.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
            },srcSetMaxWidth: 200)
        }
        warbler_1:imgixImage(url: "birds-blooms/bird-poses/bird-pose-8-1.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
            },srcSetMaxWidth: 200)
        }
        warbler_2:imgixImage(url: "birds-blooms/bird-poses/bird-pose-8-2.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
            },srcSetMaxWidth: 200)
        }
        chickadee_1:imgixImage(url: "birds-blooms/bird-poses/bird-pose-9-1.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
            },srcSetMaxWidth: 200)
        }
        chickadee_2:imgixImage(url: "birds-blooms/bird-poses/bird-pose-9-2.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
            },srcSetMaxWidth: 200)
        }
        chickadee_3:imgixImage(url: "birds-blooms/bird-poses/bird-pose-9-3.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
            },srcSetMaxWidth: 200)
        }
    }
`

export default course